import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  OnInit,
  AfterViewInit,
  OnDestroy,
  NgZone,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  AbstractControl,
} from '@angular/forms';
import { Subject, of } from 'rxjs';
import { takeUntil, tap, catchError, finalize, startWith } from 'rxjs/operators';
import { tuiCreateTimePeriods } from '@taiga-ui/kit';
import { TuiCountryIsoCode } from '@taiga-ui/i18n';
import { NgbOffcanvas } from '@ng-bootstrap/ng-bootstrap';

import { TuiAlertService, TuiNotification } from '@taiga-ui/core';
import { TranslateService } from '@ngx-translate/core';
import parsePhoneNumberFromString from 'libphonenumber-js';

import {
  Driver,
  OperativeType,
  Role,
} from 'src/app/pages/operational-staff/interface/operational-staff.model';
import { Zone, ZonesResponse } from 'src/app/models/zone/zone.model';
import { CommonSelectorV3 } from 'src/app/models/common/common.model';
import {
  DriverRequestPayload,
  DriverResponse,
} from 'src/app/models/personal/personal.model';
import { ServicesService } from 'src/app/services/service/services.service';
import { PersonalServiceService } from 'src/app/services/personal/personal-service.service';
import { HistoryServicesService } from 'src/app/services/history-services/history-services.service';
import { phoneValidator } from 'src/app/utils/phone-validator';
import { formatSecondsToHourAndMinutes } from 'src/app/utils';

import {
  DocumentType,
  DocumentTypeOptions,
} from 'src/app/enum/document-type.enum';
import { nifValidator } from 'src/app/utils/validators/nif.validator';
import { nieValidator } from 'src/app/utils/validators/nie.validator';
import { rutValidator } from 'src/app/utils/validators/rut.validator';
import { passportValidator } from 'src/app/utils/validators/passport.validator';
import { PermissionsService } from 'src/app/services/permissions/permissions.service';

declare const google: any;

const GENERIC_DOC_REGEX = /^[A-Za-z0-9]{3,20}$/;
const ROLES_WITH_REQUIRED_DRIVER_TRANSPORT = new Set<string>([
  'MOBILE_WORKSHOP',
  'DISPATCH_CAR',
]);

@Component({
  selector: 'app-modal-add-personal',
  templateUrl: './modal-add-personal.component.html',
  styleUrls: ['./modal-add-personal.component.css'],
})
export class ModalAddPersonalComponent
  implements OnInit, AfterViewInit, OnDestroy {
  @Input() driverInfo?: Driver;
  @Output() driverUpdated = new EventEmitter<void>();

  @ViewChild('startAddressInput', { static: false })
  startAddressInput!: ElementRef<HTMLInputElement>;
  @ViewChild('domicilioInput', { static: false })
  domicilioInput!: ElementRef<HTMLInputElement>;

  isOkMobility = this.permissionsService.isOkMobility();
  private isOkMobilityTenant = false;
  personalForm!: FormGroup;
  loadingZones = true;
  loadingAddOrEditData = false;
  documentTypes = DocumentTypeOptions;
  public DocumentType = DocumentType;

  readonly countriesPhone: readonly TuiCountryIsoCode[] = [
    TuiCountryIsoCode.ES,
    TuiCountryIsoCode.GB,
    TuiCountryIsoCode.FR,
    TuiCountryIsoCode.IT,
    TuiCountryIsoCode.DE,
    TuiCountryIsoCode.PT,
    TuiCountryIsoCode.NL,
    TuiCountryIsoCode.AD,
    TuiCountryIsoCode.CL,
    TuiCountryIsoCode.DO,
  ];
  countryIsoCode = TuiCountryIsoCode.ES;
  countryIsoCodeExtra = TuiCountryIsoCode.ES;

  zones: Zone[] = [];
  openElement: EventTarget | string | null = null;

  rolesGeneral: Role[] = [
    {
      name: this.translate.instant('personal.role_not_specified'),
      role: 'NOT_SPECIFIED',
    },
    { name: this.translate.instant('personal.role_regular'), role: 'REGULAR' },
    {
      name: this.translate.instant('personal.role_long_distance'),
      role: 'LONG_DISTANCE',
    },
    {
      name: this.translate.instant('personal.role_broom_car'),
      role: 'BROOM_CAR',
    },
    {
      name: this.translate.instant('personal.role_mobile_washer'),
      role: 'MOBILE_WASHER',
    },
    {
      name: this.translate.instant('personal.role_implant_washer'),
      role: 'IMPLANT_WASHER',
    },
    {
      name: this.translate.instant('personal.role_implant_office'),
      role: 'IMPLANT_OFFICE',
    },
    {
      name: this.translate.instant('personal.role_mobile_workshop'),
      role: 'MOBILE_WORKSHOP',
    },
    {
      name: this.translate.instant('personal.role_dispatch_vehicle'),
      role: 'DISPATCH_CAR',
    },
  ];

  operativeTypes: OperativeType[] = [
    {
      name: this.translate.instant('common.operativeTypes.internal'),
      value: 'Interno',
    },
    {
      name: this.translate.instant('common.operativeTypes.external'),
      value: 'Externo',
    },
  ];

  activeUsers: CommonSelectorV3[] = [
    {
      name: this.translate.instant('common.activeUsers.yes'),
      value: 'Si',
    },
    {
      name: this.translate.instant('common.activeUsers.no'),
      value: 'No',
    },
  ];

  public driverMotorcycleLicenseType: { type: string; name: string }[] = [
    {
      type: 'NOT_ALLOWED',
      name: this.translate.instant('common.motorcycleTypes.not_allowed'),
    },
    {
      type: 'UNDER_125',
      name: this.translate.instant('common.motorcycleTypes.under_125'),
    },
    {
      type: 'OVER_125',
      name: this.translate.instant('common.motorcycleTypes.over_125'),
    },
  ];

  readonly hours = tuiCreateTimePeriods(
    undefined,
    undefined,
    Array.from({ length: 60 }, (_, i) => i)
  );

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly fb: FormBuilder,
    private readonly servicesSvc: ServicesService,
    private readonly personalService: PersonalServiceService,
    private readonly historyService: HistoryServicesService,
    private readonly offcanvas: NgbOffcanvas,
    private readonly alerts: TuiAlertService,
    private readonly translate: TranslateService,
    private readonly ngZone: NgZone,
    private readonly permissionsService: PermissionsService
  ) { }

  ngOnInit(): void {
    this.initializeForm();
    this.trackOkMobilityTenant();
    this.loadZones();
    this.watchOperatorType();
    this.watchRoleDriverTransport();
    this.showAndHideDocumentNumberField();
  }

  private initializeForm(): void {
    const timePattern = /^([01]\d|2[0-3]):([0-5]\d)$/;

    this.personalForm = this.fb.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      zoneId: [null, Validators.required],
      operatorType: [null, Validators.required],
      role: [null],
      startAddress: ['', Validators.required],
      startAddressDetail: [''],
      driverTransport: [false],
      workdayStartTime: [
        '',
        [Validators.required, Validators.pattern(timePattern)],
      ],
      workdayEndTime: [
        '',
        [Validators.required, Validators.pattern(timePattern)],
      ],
      allowedMotorcycleServiceType: [null, Validators.required],
      domicilioAddress: ['', Validators.required],
      domicilioAddressExtraInfo: [''],
      identityDocumentType: [DocumentType.WithoutDocument],
      identityDocumentNumber: [''],
      mainEmail: [
        '',
        [
          Validators.required,
          Validators.pattern('^[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,4}$'),
        ],
      ],
      alternativeEmail: [
        '',
        [Validators.pattern('^[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,4}$')],
      ],
      phoneNumber: [
        '',
        [Validators.required, phoneValidator(() => this.countryIsoCode)],
      ],
      alternativePhoneNumber: [
        '',
        [phoneValidator(() => this.countryIsoCodeExtra)],
      ],
      startLatitude: [0, Validators.required],
      startLongitude: [0, Validators.required],
      isActiveUser: [null, Validators.required],
    });
  }

  private trackOkMobilityTenant(): void {
    this.isOkMobility
      .pipe(takeUntil(this.destroy$))
      .subscribe((isOkMobility) => {
        this.isOkMobilityTenant = isOkMobility;
      });
  }

  showAndHideDocumentNumberField() {
    const docTypeCtrl = this.personalForm.get('identityDocumentType')!;
    const numCtrl = this.personalForm.get('identityDocumentNumber')!;

    docTypeCtrl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((type: DocumentType) => {
        let validators = [];

        switch (type) {
          case DocumentType.NIF:
            validators = [
              Validators.required,
              nifValidator,
              Validators.maxLength(9),
            ];
            break;

          case DocumentType.NIE:
            validators = [
              Validators.required,
              nieValidator,
              Validators.maxLength(9),
            ];
            break;

          case DocumentType.Passport:
            validators = [
              Validators.required,
              passportValidator,
              Validators.maxLength(9),
            ];
            break;

          case DocumentType.RUT:
            validators = [
              Validators.required,
              rutValidator,
              Validators.maxLength(10),
            ];
            break;

          default:
            numCtrl.reset();
            numCtrl.clearValidators();
            numCtrl.updateValueAndValidity();
            return;
        }
        numCtrl.setValidators(validators);
        numCtrl.updateValueAndValidity();
      });
  }


  private loadZones(): void {
    this.servicesSvc
      .getZones()
      .pipe(
        takeUntil(this.destroy$),
        tap((resp: ZonesResponse) => {
          this.zones = resp.zones;
          if (this.driverInfo) {
            this.populateForm(this.driverInfo);
          }
        }),
        catchError((err) => {
          console.error('Error loading zones', err);
          return of(null);
        }),
        finalize(() => (this.loadingZones = false))
      )
      .subscribe();
  }

  private populateForm(driver: Driver): void {
    this.countryIsoCode = this.resolveCountry(driver.contactPhoneNumber);
    this.countryIsoCodeExtra = this.resolveCountry(
      driver.additionalContactPhoneNumber
    );

    const startSeconds = driver.workdayStartTime?.seconds ?? 0;
    const endSeconds = driver.workdayEndTime?.seconds ?? 0;

    const formattedStart = formatSecondsToHourAndMinutes(startSeconds);
    const formattedEnd = formatSecondsToHourAndMinutes(endSeconds);

    this.personalForm.patchValue({
      firstName: driver.firstName,
      lastName: driver.lastName,
      zoneId: driver.zoneId,
      operatorType: driver.isInternal ? 'Interno' : 'Externo',
      role: driver.operatorType ?? 'NOT_SPECIFIED',
      startAddress: driver.startAddress,
      startAddressDetail: driver.startAddressDetail ?? '',
      domicilioAddress: driver.startAddress,
      domicilioAddressExtraInfo: driver.startAddressDetail,
      mainEmail: driver.emailAddress,
      alternativeEmail: driver.additionalEmailAddress ?? '',
      phoneNumber: driver.contactPhoneNumber,
      alternativePhoneNumber: driver.additionalContactPhoneNumber ?? '',
      allowedMotorcycleServiceType: driver.allowedMotorcycleServiceType,
      startLatitude: driver.startAddressPoint?.Latitude ?? 0,
      startLongitude: driver.startAddressPoint?.Longitude ?? 0,
      workdayStartTime: formattedStart,
      workdayEndTime: formattedEnd,
      isActiveUser: driver.isActive ? 'Si' : 'No',
      identityDocumentType:
        driver.identityDocumentType ?? DocumentType.WithoutDocument,
      identityDocumentNumber: driver.identityDocumentNumber ?? '',
      driverTransport: driver.driverTransport === 0,
    });

    const roleControl = this.personalForm.get('role')!;
    if (this.isOkMobilityTenant || driver.isInternal) {
      roleControl.setValidators([Validators.required]);
    } else {
      roleControl.clearValidators();
      roleControl.setValue(null, { emitEvent: false });
    }
    roleControl.updateValueAndValidity({ emitEvent: false });
    this.applyRoleDriverTransportRule(roleControl.value);
  }

  limitLength(e: Event) {
    const v = (e.target as HTMLInputElement).value || '';
    if (v.length > 32) {
      (e.target as HTMLInputElement).value = v.slice(0, 32);
      this.personalForm
        .get('identityDocumentNumber')
        ?.setValue(v.slice(0, 32), { emitEvent: false });
    }
  }

  private watchOperatorType(): void {
    this.personalForm
      .get('operatorType')!
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        const roleControl = this.personalForm.get('role')!;
        if (this.isOkMobilityTenant || value === 'Interno') {
          roleControl.setValidators([Validators.required]);
        } else {
          roleControl.clearValidators();
          this.applyRoleDriverTransportRule(null);
        }
        roleControl.updateValueAndValidity({ emitEvent: false });
      });
  }

  private watchRoleDriverTransport(): void {
    const roleControl = this.personalForm.get('role');

    roleControl?.valueChanges
      .pipe(startWith(roleControl.value), takeUntil(this.destroy$))
      .subscribe((roleValue: string | null) => {
        this.applyRoleDriverTransportRule(roleValue);
      });
  }

  private applyRoleDriverTransportRule(roleValue: string | null): void {
    const driverTransportControl = this.personalForm.get('driverTransport');
    const shouldLockDriverTransport =
      !!roleValue && ROLES_WITH_REQUIRED_DRIVER_TRANSPORT.has(roleValue);

    if (shouldLockDriverTransport) {
      driverTransportControl?.setValue(true, { emitEvent: false });
      driverTransportControl?.disable({ emitEvent: false });
      return;
    }

    driverTransportControl?.enable({ emitEvent: false });
  }

  private resolveCountry(phone = ''): TuiCountryIsoCode {
    const isoKey = this.historyService.getCountryISO(
      phone
    ) as keyof typeof TuiCountryIsoCode;
    return TuiCountryIsoCode[isoKey] || TuiCountryIsoCode.ES;
  }

  onTimeInput(type: 'start' | 'end'): void {
    const controlName =
      type === 'start' ? 'workdayStartTime' : 'workdayEndTime';
    const control = this.personalForm.get(controlName) as AbstractControl;
    if (!control) {
      return;
    }

    const raw = (control.value || '').replace(/\D/g, '');
    if (raw.length < 3) {
      control.setValue(raw, { emitEvent: false });
      return;
    }

    const formatted = `${raw.slice(0, 2)}:${raw.slice(2, 4)}`;
    const isValid = /^([01]\d|2[0-3]):([0-5]\d)$/.test(formatted);
    if (isValid) {
      control.setValue(formatted, { emitEvent: false });
    } else if (raw.length < 5) {
      control.setValue(raw, { emitEvent: false });
    } else {
      control.setValue('', { emitEvent: false });
    }
  }

  ngAfterViewInit(): void {
    if (!google?.maps?.places) {
      console.error('Google Maps Places API not loaded');
      return;
    }
    this.setupAutocomplete(
      this.startAddressInput.nativeElement,
      'startAddress',
      true
    );
    this.setupAutocomplete(
      this.domicilioInput.nativeElement,
      'domicilioAddress',
      false
    );
  }

  private setupAutocomplete(
    element: HTMLInputElement,
    controlName: 'startAddress' | 'domicilioAddress',
    includeCoords: boolean
  ): void {
    const autocomplete = new google.maps.places.Autocomplete(element, {
      types: ['address'],
    });
    autocomplete.addListener('place_changed', () => {
      this.ngZone.run(() => {
        const place: google.maps.places.PlaceResult = autocomplete.getPlace();
        if (!place.geometry) {
          console.error('Selected place has no geometry');
          return;
        }
        const formatted = place.formatted_address || '';
        const patch: Partial<{
          startLatitude: number;
          startLongitude: number;
        }> = {};
        if (includeCoords) {
          patch.startLatitude = place.geometry.location.lat();
          patch.startLongitude = place.geometry.location.lng();
        }
        this.personalForm.patchValue({ [controlName]: formatted, ...patch });
      });
    });
  }

  normalizePhoneNumber(
    controlName: 'phoneNumber' | 'alternativePhoneNumber'
  ): void {
    const control = this.personalForm.get(controlName);
    if (!control) {
      return;
    }
    const raw = control.value as string;
    if (!raw) {
      return;
    }

    try {
      const parsed = parsePhoneNumberFromString(raw, this.countryIsoCode);
      if (parsed?.isValid()) {
        control.setValue(parsed.formatInternational(), { emitEvent: false });
      } else {
        control.setErrors({ invalidPhone: true });
      }
    } catch {
      control.setErrors({ invalidPhone: true });
    }
  }

  savePersonal(): void {
    const hiddenInternalControl = this.personalForm.get('operatorType');
    if (this.isOkMobilityTenant) {
      hiddenInternalControl?.clearValidators();
    } else {
      hiddenInternalControl?.setValidators([Validators.required]);
    }
    hiddenInternalControl?.updateValueAndValidity({ emitEvent: false });

    this.personalForm.updateValueAndValidity({ emitEvent: false });
    this.personalForm.markAllAsTouched();

    if (this.personalForm.invalid) {
      this.alerts
        .open(
          this.translate.instant('modals.add_personal.fill_required_fields'),
          {
            label: this.translate.instant(
              'modals.add_personal.form_incomplete'
            ),
            status: 'warning',
            autoClose: true,
          }
        )
        .subscribe();
      return;
    }

    this.loadingAddOrEditData = true;
    const payload = this.buildPayload();
    const request$ = this.driverInfo
      ? this.personalService.updatePersonal(payload)
      : this.personalService.addpersonal(payload);

    request$
      .pipe(
        takeUntil(this.destroy$),
        tap((res: DriverResponse) => {
          this.loadingAddOrEditData = false;
          this.driverUpdated.next();
          this.offcanvas.dismiss();
          const key = this.driverInfo
            ? 'modals.add_personal.success_saved'
            : 'modals.add_personal.success_created';
          this.alerts
            .open(this.translate.instant(key), {
              status: 'success',
              autoClose: true,
            })
            .subscribe();

          // ER - 05/06/2025 - Pending to check the backend
          // if (!res.wasSuccessful) {
          //   this.loadingAddOrEditData = false;

          //   if (res.statusCode) {
          //     this.historyService.showErrorCode(res.statusCode);
          //   }
          //   throw new Error(res.message || 'Unknown error');
          // }
        }),
        tap(() => { }),
        catchError((err) => {
          this.loadingAddOrEditData = false;

          const isServerError = err.status === 500;
          const messageKey = isServerError
            ? 'modals.add_personal.server_error'
            : 'modals.add_personal.other_error';
          const labelKey = isServerError
            ? 'modals.add_personal.error_500'
            : 'modals.add_personal.other_error';
          this.alerts
            .open(this.translate.instant(messageKey), {
              label: this.translate.instant(labelKey),
              status: 'error',
              autoClose: true,
            })
            .subscribe();
          return of(null);
        }),
        finalize(() => (this.loadingAddOrEditData = false))
      )
      .subscribe();
  }

  private parseTimeToUtcTimestamp(hhmm: string): number {
    const [h, m] = hhmm.split(':').map(Number);
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth(); // 0–11
    const day = now.getDate(); // 1–31
    const msUtc = Date.UTC(year, month, day, h, m, 0);
    return Math.floor(msUtc / 1000);
  }

  private buildPayload(): DriverRequestPayload {
    const form = this.personalForm.getRawValue();
    const isInternal = this.isOkMobilityTenant || form.operatorType === 'Interno';

    const secondsStart = this.parseTimeToUtcTimestamp(form.workdayStartTime);
    const secondsEnd = this.parseTimeToUtcTimestamp(form.workdayEndTime);

    const payload: DriverRequestPayload = {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      zoneId: form.zoneId,
      operatorType: form.role || 'NOT_SPECIFIED',
      isInternal,
      role: form.role || undefined,
      startAddress: form.startAddress,
      startAddressDetail: form.startAddressDetail || undefined,
      domicilioAddress: form.domicilioAddress,
      domicilioAddressExtraInfo: form.domicilioAddressExtraInfo || undefined,
      mainEmail: form.mainEmail.trim(),
      alternativeEmail: form.alternativeEmail?.trim() || form.mainEmail.trim(),
      phoneNumber: form.phoneNumber,
      alternativePhoneNumber: form.alternativePhoneNumber || '',
      startLatitude: form.startLatitude,
      startLongitude: form.startLongitude,
      workdayStartTime: { seconds: secondsStart },
      workdayEndTime: { seconds: secondsEnd },
      allowedMotorcycleServiceType: form.allowedMotorcycleServiceType,
      isActiveUser: form.isActiveUser === 'Si',
      // ER - 05/06/2025 - Pending to check the backend
      identityDocumentType: form.identityDocumentType,
      identityDocumentNumber: form.identityDocumentNumber.trim(),
      driverTransport: form.driverTransport ? 0 : 1,
    };

    if (this.driverInfo?.driverId) {
      payload.driverId = this.driverInfo.driverId;
    }
    return payload;
  }

  closeCanvas(): void {
    this.offcanvas.dismiss();
  }

  private getRoleName(roleValue: string): string {
    return (
      this.rolesGeneral.find((r) => r.role === roleValue)?.name ||
      this.rolesGeneral[0].name
    );
  }

  openPhoneContainer(clickEvent: MouseEvent): void {
    const clickedElement = clickEvent.currentTarget as HTMLElement;

    if (clickedElement === this.openElement) {
      this.openElement = null;
    }

    if (clickedElement !== this.openElement) {
      const dropdownElements: HTMLCollectionOf<HTMLElement> =
        document.getElementsByTagName(
          'tui-dropdown'
        ) as HTMLCollectionOf<HTMLElement>;

      if (dropdownElements.length > 0 && dropdownElements[0] !== undefined) {
        const foundDropdownElement: HTMLElement = dropdownElements[0];
        const targetContainer: HTMLElement | null = document.getElementById(
          'modal_show_countries_phones'
        );

        if (targetContainer) {
          targetContainer.innerHTML = '';
          targetContainer.append(foundDropdownElement);
        } else {
          console.warn(
            "El contenedor 'modal_show_countries_phones' no fue encontrado en el DOM."
          );
        }
      } else {
        console.warn("No se encontraron elementos 'tui-dropdown' en el DOM.");
      }

      this.openElement = clickedElement;
    } else {
      this.openElement = null;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

}
